import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import type { WorkspaceSearcher } from '@/features/iam/workspaces/WorkspaceSearcher'
import { action, computed, observable } from 'mobx'
import { orderBy } from 'lodash-es'
import type { Deactivatable } from '@/infra/activation'

/**
 * View model for managing the OCR scope work workspace selector.
 */
export class OCRScopeWorkWorkspaceSearchViewModel
  implements WorkspaceSearcher, Deactivatable
{
  /**
   * User is searching a workspace.
   */
  @observable accessor editing = false

  @observable accessor searchText = ''

  constructor(
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
  ) {}

  /**
   * Workspaces where the current user may view scope work jobs.
   */
  @computed
  get workspacesWhereUserCanViewScopeWorkJobs() {
    return orderBy(
      this._getCurrentUserWorkspaceMemberships()
        .filter(({ member }) => {
          return member.permissions.adminScopeWorkView
        })
        .map((m) => m.workspace),
      (ws) => ws.name.toLowerCase(),
      'asc',
    )
  }

  /**
   * Filtered workspaces.
   */
  @computed
  get filteredWorkspaces() {
    const result = this.workspacesWhereUserCanViewScopeWorkJobs

    if (this.searchText) {
      const searchText = this.searchText.toLowerCase()

      return result.filter((ws) =>
        ws.name.toLocaleLowerCase().includes(searchText),
      )
    }
    return result
  }

  @action.bound
  setSearchText(searchText: string) {
    this.searchText = searchText
  }

  @action.bound
  startEditing() {
    this.editing = true
  }

  @action.bound
  stopEditing() {
    this.editing = false
  }

  /** @inheritDoc */
  deactivate() {
    this.setSearchText('')
  }
}
